/* eslint-disable max-lines-per-function */
/* eslint-disable no-irregular-whitespace */
import type { Space } from '@mail/cross-sizes-utils';
import { Icon28BlockOutline } from '@vkontakte/icons';
import { Spacing } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { createRef, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import {
    overquotaCommunicationPayPlacement,
    overquotaDisabledFeaturesModalConfig,
} from 'reactApp/appHelpers/featuresHelpers/features/overquotaCommunications';
import { useBuyClickHandlers } from 'reactApp/hooks/useBuyClickHandlers';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { goToPromoQuota } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import Content from 'reactApp/ui/Content/Content';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { DisabledFeature } from 'reactApp/ui/DisabledFeatureDialog/DisabledFeatureDialog.types';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { createGaSender } from 'reactApp/utils/ga';
import { scrollLock, scrollUnlock } from 'reactApp/utils/scrollLock';

import styles from './DisabledFeatureDialog.css';

const sendGa = createGaSender('overqouta');

const getTitle = ({ disabledFeature, isBizUser }: { disabledFeature: DisabledFeature; isBizUser: boolean }) => {
    let variablePart = '';
    if (isBizUser) {
        switch (disabledFeature) {
            case DisabledFeature.publish:
                variablePart = 'публичного доступа';
                break;
            case DisabledFeature.newFile:
                variablePart = 'создания новых файлов';
                break;
            case DisabledFeature.createAlbum:
                variablePart = 'создания альбома';
                break;
            default:
                variablePart = 'создания новых папок';
                break;
        }

        return `Функция ${variablePart} отключена`;
    }

    switch (disabledFeature) {
        case DisabledFeature.publish:
            variablePart = 'Делиться файлами';
            break;
        case DisabledFeature.newFile:
            variablePart = 'Создать файл';
            break;
        case DisabledFeature.newFolder:
            variablePart = 'Создать папку';
            break;
        case DisabledFeature.createAlbum:
            variablePart = 'Создать альбом';
            break;
        default:
            variablePart = 'Создать папку';
            break;
    }

    return `${variablePart} пока нельзя`;
};

export interface Props {
    disabledFeature: DisabledFeature;
    overquota: Space;
    onClose();
    onBuy();
    isBizUser: boolean;
}

const hasClearButton = overquotaDisabledFeaturesModalConfig.clearButton;

export const DisabledFeatureDialog = ({ disabledFeature, onClose, onBuy, isBizUser }: Props) => {
    const contentRef = createRef<HTMLDivElement>();
    const overquota = useSelector(UserQuotaSelectors.getOverQuota);

    const { showPaywall } = useBuyClickHandlers({ paySource: 'mnz_overquota_disabled_feature' });

    useEffect(() => {
        sendGa(disabledFeature, 'show');

        if (contentRef.current) {
            scrollLock(contentRef.current);
        }

        return () => {
            if (contentRef.current) {
                scrollUnlock(contentRef.current);
            }
        };
    }, []);

    const handleClose = useCallback(() => {
        sendGa(disabledFeature, 'close-click', null, true);
        onClose();
    }, []);

    const handleBuy = useCallback(() => {
        sendGa(disabledFeature, 'buy-click', null, true);

        if (overquotaCommunicationPayPlacement === 'paywall') {
            onClose();
            showPaywall();
            return;
        }

        onBuy();
    }, []);

    const footer = useMemo(() => {
        return (
            <div className={styles.footer}>
                <div className={styles.buttonWrapper}>
                    <Button
                        className={styles.button}
                        data-name="action"
                        theme="octavius"
                        sizeMode={IS_PHONE_BROWSER ? ButtonSizeMode.extraBig : ButtonSizeMode.small}
                        primary={true}
                        error={false}
                        onClick={handleBuy}
                    >
                        Снять ограничение
                    </Button>
                    {hasClearButton && (
                        <Button
                            className={styles.button}
                            data-name="secondary-action"
                            theme="octavius"
                            sizeMode={IS_PHONE_BROWSER ? ButtonSizeMode.extraBig : ButtonSizeMode.small}
                            onClick={() => {
                                goToPromoQuota({ hash: 'cleaner' });
                            }}
                        >
                            Удалить лишнее
                        </Button>
                    )}
                </div>
            </div>
        );
    }, []);

    const description = useMemo(() => {
        if (isBizUser) {
            return (
                <>
                    Объем вашего Облака превышен на <strong>{overquota.value}</strong>. Доступ к вашим файлам ограничен, но скачивание
                    и удаление остаются доступными.
                </>
            );
        }

        let feature = '';
        switch (disabledFeature) {
            case DisabledFeature.publish:
                feature = 'снова делиться файлами';
                break;
            case DisabledFeature.newFile:
                feature = 'создавать файлы';
                break;
            case DisabledFeature.newFolder:
                feature = 'создавать папки';
                break;
            case DisabledFeature.createAlbum:
                feature = 'создавать альбомы';
                break;
        }
        return (
            <>
                Единое пространство Облака и Почты переполнено на <span>{overquota.value}</span> — увеличьте его с подпиской Mail Space
                {hasClearButton ? ' или удалите лишнее' : ''}, чтобы {feature}
            </>
        );
    }, [overquota.value, disabledFeature, isBizUser]);

    const content = useMemo(() => {
        return (
            <>
                <div className={classNames({ [styles.content]: true, [styles.content_scrollable]: true })} ref={contentRef}>
                    <div className={styles.iconWrapper}>
                        <Icon28BlockOutline />
                    </div>
                    <div className={styles.title}>{getTitle({ disabledFeature, isBizUser })}</div>
                    <div className={styles.description}>{description}</div>
                    {isBizUser && (
                        <div className={styles.recommend}>
                            {'Обратитесь к администратору домена или удалите лишние файлы, освободив место в\u00A0Облаке.'}
                        </div>
                    )}
                </div>
                {!isBizUser ? footer : <Spacing size={32} />}
            </>
        );
    }, [isBizUser]);

    if (IS_PHONE_BROWSER) {
        return (
            <MobileDialog id="disabledFeature-dialog" onClose={handleClose} closeOnDimmerClick={true} mod="confirm" topmost>
                <div className={classNames(styles.root_phone, styles.rebranding)}>{content}</div>
            </MobileDialog>
        );
    }

    return (
        <Dialog
            open
            onCancel={handleClose}
            disableScrollOnBody={false}
            id="disabledFeature-dialog"
            className={classNames(styles.root, styles.rebranding)}
            size={'tiny'}
        >
            <Content isModal isLoading={false} hasError={false} scrolling>
                {content}
            </Content>
        </Dialog>
    );
};
